// ===========================================================================
// Choosing the picture that goes with a report.
//
// The reporter's snapshot of the person they are reporting was encoded at the
// video's native size, JPEG quality 0.9, with no limit. From a 4K camera that
// could be larger than the server took with a report, and the socket closes a
// connection on a message that size before reading it: pressing Report
// disconnected the reporter and the report never arrived.
//
// So the picture is tried at native size and full quality first, then at
// lower quality, and only when even that will not fit, smaller. STANDING RULE
// (the operator, 2026-09-15): a picture a person reviews keeps the size the
// camera made it; the server's ceiling (MAX_IMAGE_B64 in server/src/session.js,
// about 12 MB of JPEG) is large enough that the shrinking steps are a backstop
// for a camera nobody has met yet, not a path a real frame takes. If nothing
// fits, the report goes without a picture. It always goes. Kept apart from
// app.js, with no page in it, so it can be tested on its own. Also used for
// the still of a shared file attached to a report (ui/chat.js). ASCII only.
// ===========================================================================

/**
 * The most base64 a report's picture may be. Under the server's 16,000,000 by
 * enough to leave room for the rest of the message and a little over.
 */
export const REPORT_PICTURE_B64_BUDGET = 15_000_000;

/**
 * [scale, quality] to try, in order: native size at full quality, the quality
 * down, and only then the dimensions. A 4K frame fits at native size well
 * before the last quality step.
 */
export const REPORT_PICTURE_STEPS = Object.freeze([
  Object.freeze([1, 1]),
  Object.freeze([1, 0.9]),
  Object.freeze([1, 0.8]),
  Object.freeze([1, 0.7]),
  Object.freeze([1, 0.6]),
  Object.freeze([0.85, 0.6]),
  Object.freeze([0.7, 0.6]),
]);

/**
 * The picture to send with a report, or null to send the report without one.
 *
 * @param {(scale: number, quality: number) => string|null} encode  the snapshot
 *   as base64 JPEG at that fraction of its native size and that quality, or
 *   null when it could not be encoded
 * @returns {string|null}
 */
export function pickReportPicture(encode) {
  for (const [scale, quality] of REPORT_PICTURE_STEPS) {
    const picture = encode(scale, quality);
    if (picture && picture.length <= REPORT_PICTURE_B64_BUDGET) return picture;
  }
  return null;
}

export default { pickReportPicture, REPORT_PICTURE_B64_BUDGET, REPORT_PICTURE_STEPS };
